import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  Typography,
  TableRow,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { softwareApi } from '../../api/systemRegisterApi';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'business-case-form';
const PRINTING_BODY_CLASS = 'sr-printing-business-case-form';

const CONTENT_ROWS = [
  { name: 'projectOverview', label: '1. Project Overview:' },
  { name: 'backgroundCurrentSituation', label: '2. Background / Current Situation:' },
  { name: 'proposedSolution', label: '3. Proposed Solution:' },
  { name: 'technicalRequirementsIntegration', label: '4. Technical Requirements and Integration:' },
  { name: 'securityComplianceConsiderations', label: '5. Security and Compliance Considerations:' },
  { name: 'implementationPlan', label: '6. Implementation Plan:' },
  { name: 'costEstimate', label: '7. Cost Estimate (CapEx / OpEx):' },
  { name: 'benefitsImpactsOnUsers', label: '8. Benefits / Impacts on Users:' },
  { name: 'performanceMetrics', label: '9. Performance Metrics:' },
  { name: 'risksAndLimitations', label: '10. Risks and Limitations:' },
];

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

function formatDisplayDate(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

/**
 * Paper-style Business Case form view (same pattern as Change Management Form).
 * Does not use the software wizard chrome (System Register / system banner / Report step).
 */
export default function BusinessCaseViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const querySoftwareId = searchParams.get('softwareId');
  const returnTo = location.state?.returnTo || '';

  const [softwareName, setSoftwareName] = useState(location.state?.softwareName || '');
  const [businessCase, setBusinessCase] = useState(() => location.state?.businessCase || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (location.state?.businessCase) {
      setBusinessCase(location.state.businessCase);
      setSoftwareName(location.state.softwareName || '');
      setLoadError('');
      return;
    }

    if (!querySoftwareId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await softwareApi.get(querySoftwareId);
        const detail = unwrapData(response);
        if (cancelled) return;
        if (!detail?.businessCase) {
          setBusinessCase(null);
          setLoadError('No business case found for this software.');
          return;
        }
        setBusinessCase(detail.businessCase);
        setSoftwareName(detail?.softwareName || '');
      } catch (error) {
        if (!cancelled) {
          setBusinessCase(null);
          setLoadError(apiErrorMessage(error, 'Failed to load business case.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.state, querySoftwareId]);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const goBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate(-1);
  };

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !businessCase || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const labelBase = (softwareName || 'business-case')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'business-case';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-BUSINESS-CASE-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [businessCase, downloadingPdf, softwareName]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-business-case-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          border: none !important;
          box-shadow: none !important;
          overflow: visible !important;
          background: #fff !important;
          color: #111 !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!businessCase) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Business Case Form" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError || 'Open a business case from the Use Case step using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={goBack}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Business Case Form" />
      </Box>

      <Box className="no-print" display="flex" justifyContent="flex-end" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Button type="button" startIcon={<ArrowBackIcon />} onClick={goBack} variant="outlined">
          Back
        </Button>
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}` : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Business Case Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR BC1
            </Typography>
          </Box>
        </Box>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Software / System Name:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(softwareName) || '—'}
              </FieldCell>
              <FieldCell label="Submitted by:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(businessCase.submittedBy) || '—'}
              </FieldCell>
              <FieldCell label="Date:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatDisplayDate(businessCase.caseDate) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Title / Role:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(businessCase.titleRole) || '—'}
              </FieldCell>
            </TableRow>

            {CONTENT_ROWS.map((row) => (
              <TableRow key={row.name}>
                <FieldCell label={row.label} border={form.border} muted={form.muted} colSpan={3}>
                  <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(businessCase[row.name]) || '—'}</Box>
                </FieldCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
